import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Seo, orgJsonLd } from "../lib/seo";
import { landingGroups, categoryUrl } from "../components/landingData";
import { versionedImageUrl } from "../lib/imageUrl";
import "./Home.css";

function CategoryCarousel({
  group,
  index,
}: {
  group: (typeof landingGroups)[number];
  index: number;
}) {
  const [position, setPosition] = useState(0);
  const touch = useRef(0);
  const product = group.slides[position];
  const move = (delta: number) =>
    setPosition((p) => (p + delta + group.slides.length) % group.slides.length);
  return (
    <article className="family">
      <span className="family-number">0{index + 1}</span>
      <div
        className="carousel-photo"
        onTouchStart={(e) => {
          touch.current = e.changedTouches[0].clientX;
        }}
        onTouchEnd={(e) => {
          const d = e.changedTouches[0].clientX - touch.current;
          if (Math.abs(d) > 45) move(d < 0 ? 1 : -1);
        }}
      >
        <Link
          to={categoryUrl(group.name)}
          aria-label={`Explorar ${group.name}`}
        >
          <img src={versionedImageUrl(product.url)} alt={product.name} loading="lazy" />
        </Link>
        <button
          className="carousel-prev"
          aria-label={`Producto anterior de ${group.name}`}
          onClick={() => move(-1)}
        >
          ‹
        </button>
        <button
          className="carousel-next"
          aria-label={`Siguiente producto de ${group.name}`}
          onClick={() => move(1)}
        >
          ›
        </button>
      </div>
      <div className="carousel-info" aria-live="polite">
        <div className="carousel-product">
          <span>{product.name}</span>
          <Link
            className="direct-product"
            to={`/producto/${encodeURIComponent(product.sku)}`}
          >
            Ver producto
          </Link>
        </div>
        <span>
          {position + 1} / {group.slides.length}
        </span>
      </div>
      <Link className="category-title" to={categoryUrl(group.name)}>
        <strong>{group.name}</strong>

      </Link>
      <p>{group.description}</p>
    </article>
  );
}
export function Home() {
  return (
    <>
      <Seo
        title="NULED · Iluminación LED mayorista en México · Distribuidor Lutron"
        description="NULED importa y especifica iluminación LED para distribuidores, electricistas e integradores en México. Distribuidor mayorista autorizado Lutron."
        keywords="iluminacion led mexico, luminarias led mayorista, downlight mexico, riel magnetico mexico, fabricante luminarias led, distribuidor lutron mexico"
        canonical="https://nuled.mx/"
        jsonLd={[
          orgJsonLd,
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "NULED",
            url: "https://nuled.mx",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://nuled.mx/catalogo?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      <Header visualNav />
      <main className="home-design">
        <section className="hero">
          <img
            src="https://sociallight.co.nz/cdn/shop/files/Outside_lights_2.jpg?v=1743044241&width=3115"
            alt="Fachada nocturna de madera realzada con iluminación arquitectónica cálida"
          />
          <div className="hero-copy">
            <div className="eyebrow">Luz que transforma la arquitectura</div>
            <h1>
              Tu espacio.
              <br />
              Tu visión.
              <br />
              <strong>La luz correcta.</strong>
            </h1>
            <p>
              Encuentra luminarias que realzan cada detalle. Te acompañamos
              desde la selección hasta la especificación de tu proyecto.
            </p>
            <div className="actions">
              <a className="btn" href="#familias">
                Explorar luminarias
              </a>
              <a className="btn line" href="#asesoria">
                Cotizar un proyecto
              </a>
            </div>
          </div>
          <div className="hero-label">
            REFERENCIA VISUAL / SOCIALLIGHT · ILUMINACIÓN EXTERIOR
          </div>
        </section>
        <div className="strip">
          <div>
            Asesoría de especificación
          </div>
          <div>
            <span>◌</span>Variantes para cada proyecto
          </div>
          <div>
            Atención en México
          </div>
          <div>
            <span>◌</span>Iluminación + control
          </div>
        </div>
        <section className="section" id="familias">
          <div className="heading">
            <div>
              <div className="eyebrow">Catálogo NULED</div>
              <h2>Encuentra tu luminaria.</h2>
            </div>
            <Link className="text-link" to="/catalogo">
              Ver catálogo completo
            </Link>
          </div>
          <div className="families">
            {landingGroups.map((group, index) => (
              <CategoryCarousel key={group.name} group={group} index={index} />
            ))}
          </div>
        </section>
        <div className="products-section">
          <section className="section effects-section" id="seleccion">
            <div className="heading">
              <div>
                <div className="eyebrow">Diseña con luz</div>
                <h2>
                  ¿Cómo quieres iluminar
                  <br />
                  tu espacio?
                </h2>
              </div>
              <p className="effects-intro">
                Empieza por el efecto que quieres lograr.
                <br />
                Encuentra las luminarias para hacerlo realidad.
              </p>
            </div>
            <div className="light-effects">
              <a
                className="light-effect"
                href="/catalogo?path=Mangueras%20y%20tiras%20LED"
                target="_blank"
                rel="noopener"
              >
                <div className="effect-photo">
                  <img
                    src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/fe029fac-de00-4623-8336-e50420035339/30ef45af-196f-461e-86d9-960f5156d5c7.jpg"
                    alt="Luz indirecta: ambiente de referencia del catálogo"
                    loading="lazy"
                  />
                  <span className="effect-number">01</span>
                </div>
                <h3>Luz indirecta</h3>
                <p>Una luz que se siente, sin ver de dónde viene.</p>
                <span className="effect-link">
                  Explorar soluciones
                </span>
              </a>
              <a
                className="light-effect"
                href="/catalogo?path=Exterior"
                target="_blank"
                rel="noopener"
              >
                <div className="effect-photo">
                  <img
                    src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/a816aa65-f67f-4d12-bcdd-67e2ed05019f/7546fc19-bb5a-4b10-a546-099f602d1920.jpg"
                    alt="Exteriores: ambiente de referencia del catálogo"
                    loading="lazy"
                  />
                  <span className="effect-number">02</span>
                </div>
                <h3>Exteriores</h3>
                <p>Ilumina fachadas, jardines y recorridos.</p>
                <span className="effect-link">
                  Explorar soluciones
                </span>
              </a>
              <a
                className="light-effect"
                href="/catalogo?path=Downlights"
                target="_blank"
                rel="noopener"
              >
                <div className="effect-photo">
                  <img
                    src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/53d84d5c-cdd5-4e17-9dab-ef0ef300c2af/9cccb195-9534-4dee-8429-00cdd78f97bf.jpg"
                    alt="Luz de acento: ambiente de referencia del catálogo"
                    loading="lazy"
                  />
                  <span className="effect-number">03</span>
                </div>
                <h3>Luz de acento</h3>
                <p>Destaca un detalle. Dale profundidad al espacio.</p>
                <span className="effect-link">
                  Explorar soluciones
                </span>
              </a>
              <a
                className="light-effect"
                href="/catalogo?path=Riel%20Magn%C3%A9tico"
                target="_blank"
                rel="noopener"
              >
                <div className="effect-photo">
                  <img
                    src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/8fd3c083-ead2-4047-8e4c-8882fbf6355f/779672f1-8e8e-46da-9965-1037a0d8b8cb.png"
                    alt="Luz flexible: ambiente de referencia del catálogo"
                    loading="lazy"
                  />
                  <span className="effect-number">04</span>
                </div>
                <h3>Luz flexible</h3>
                <p>Combina, orienta y adapta la luz a tu espacio.</p>
                <span className="effect-link">
                  Explorar soluciones
                </span>
              </a>
            </div>
          </section>
        </div>
        <section className="section" id="espacios">
          <div className="heading">
            <div>
              <div className="eyebrow">Inspírate</div>
              <h2>
                Piensa en el espacio.
                <br />
                Nosotros pensamos en la luz.
              </h2>
            </div>
            <span style={{ fontSize: "12px", color: "var(--muted)" }}>
              Ambientes del catálogo NULED
            </span>
          </div>
          <div className="spaces">
            <a
              className="space"
              href="/catalogo?path=Mangueras%20y%20tiras%20LED"
            >
              <img
                loading="lazy"
                src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/4fa41e8f-ee1c-4b4c-bd82-a6507ca5d394/5f54e4ec-b2a2-4ece-bafc-7635c8cde2c0.jpg"
                alt="Cocina contemporánea con materiales claros y luz integrada"
              />
              <div className="space-copy">
                <h3>Espacios para habitar.</h3>
                <span>Explorar iluminación lineal</span>
              </div>
            </a>
            <a className="space" href="/catalogo?path=Downlights">
              <img
                loading="lazy"
                src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/0d29fe7e-26bc-45c9-8189-020139452c3e/a1c5f5e7-d55d-4e3a-b1a6-d473a27942a2.jpg"
                alt="Baño de tonos neutros e iluminación de acento"
              />
              <div className="space-copy">
                <h3>Detalles que importan.</h3>
                <span>Explorar downlights</span>
              </div>
            </a>
            <a
              className="space"
              href="/catalogo?cat=exterior"
              target="_blank"
              rel="noopener"
            >
              <img
                loading="lazy"
                src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/a816aa65-f67f-4d12-bcdd-67e2ed05019f/7546fc19-bb5a-4b10-a546-099f602d1920.jpg"
                alt="Jardín y muro iluminados al anochecer"
              />
              <div className="space-copy">
                <h3>Más allá del interior.</h3>
                <span>Explorar exterior</span>
              </div>
            </a>
          </div>
        </section>
        <section className="feature" id="control">
          <img
            className="feature-img"
            loading="lazy"
            src="https://oorhorokbvnsgoqsqwqq.supabase.co/storage/v1/object/public/product-images/4fa41e8f-ee1c-4b4c-bd82-a6507ca5d394/5f54e4ec-b2a2-4ece-bafc-7635c8cde2c0.jpg"
            alt="Ambiente residencial con iluminación arquitectónica"
          />
          <div className="feature-copy">
            <div className="eyebrow">Iluminación NULED + control Lutron</div>
            <h2>
              La atmósfera correcta,
              <br />
              en cada momento.
            </h2>
            <p>
              Integra luminarias y control en una misma propuesta. Te ayudamos a
              seleccionar el sistema y a confirmar la compatibilidad de cada
              combinación.
            </p>
            <a className="btn" href="/lutron" target="_blank" rel="noopener">
              Explorar soluciones Lutron
            </a>
          </div>
        </section>
        <section className="section service" id="asesoria">
          <div>
            <div className="eyebrow">De los planos a la luz</div>
            <h2>
              Hagamos realidad
              <br />
              tu próximo proyecto.
            </h2>
            <p className="subheading">
              Comparte tus planos, referencias o lista de luminarias. Nuestro
              equipo te ayuda a elegir y cotizar la solución para tu espacio.
            </p>
            <div className="actions">
              <a
                className="btn"
                href="https://wa.me/525549332582?text=Hola%20NULED,%20necesito%20asesor%C3%ADa%20para%20mi%20proyecto"
              >
                Hablar con un especificador
              </a>
            </div>
          </div>
          <div className="steps">
            <div className="step">
              <span>01</span>
              <div>
                <h3>Cuéntanos qué estás creando</h3>
                <p>Tipo de espacio, ubicación y necesidades del proyecto.</p>
              </div>
            </div>
            <div className="step">
              <span>02</span>
              <div>
                <h3>Definimos la solución contigo</h3>
                <p>Luminarias, temperatura de color, ópticas y control.</p>
              </div>
            </div>
            <div className="step">
              <span>03</span>
              <div>
                <h3>Recibe tu cotización</h3>
                <p>
                  Productos y variantes con disponibilidad y plazo confirmados.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
